import { useState, useCallback } from 'react';
import logger from '../utils/logger';
import { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { Case, LandTransporter } from '../types';
import { DragDropService, DragDropState, DragDropResult } from '../services/dragDropService';
import { ScheduleValidationService, ValidationResult } from '../services/scheduleValidationService';

/**
 * ドラッグ&ドロップスケジュール操作のためのフック設定
 */
export interface UseDragDropScheduleConfig {
  cases: Case[];
  availableTransporters: LandTransporter[];
  selectedDate: string;
  selectedDayOfWeek: keyof LandTransporter['workingDays'];
  onCaseUpdate: (caseId: string, updates: Partial<Case>) => Promise<void>;
  onOperationComplete?: (result: DragDropResult) => void;
  onValidationError?: (error: ValidationResult) => void;
}

/**
 * ドラッグ&ドロップスケジュール操作のフック戻り値
 */
export interface UseDragDropScheduleReturn extends DragDropState {
  handleDragStart: (event: DragStartEvent) => void;
  handleDragOver: (event: DragOverEvent) => void;
  handleDragEnd: (event: DragEndEvent) => Promise<void>;
  resetDragState: () => void;
  validateCaseAssignment: (case_: Case, transporterId: string) => Promise<ValidationResult>;
}

/**
 * スケジュールのドラッグ&ドロップ操作を管理するカスタムフック
 * FL-55のリファクタリング: ドラッグ&ドロップロジックをカスタムフックに分離
 *
 * @param config - フックの設定
 * @returns ドラッグ&ドロップの状態とハンドラー
 */
export const useDragDropSchedule = (
  config: UseDragDropScheduleConfig
): UseDragDropScheduleReturn => {
  const {
    cases,
    availableTransporters,
    selectedDate,
    onCaseUpdate,
    onOperationComplete,
    onValidationError,
  } = config;

  // ドラッグ&ドロップの状態管理
  const [dragState, setDragState] = useState<DragDropState>(DragDropService.getInitialState());

  /**
   * ドラッグ開始時のハンドラー
   */
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const activeCase = DragDropService.handleDragStart(event);
    setDragState(prevState => ({
      ...prevState,
      activeCase,
    }));
  }, []);

  /**
   * ドラッグオーバー時のハンドラー
   * 注: dnd-kit の DragOverEvent は同期ハンドラーのため、
   * バリデーションは簡易チェック（Schedule存在確認）のみ実施し、
   * 詳細なバリデーションは handleDragEnd で実施します
   */
  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const dropTarget = DragDropService.handleDragOver(event);

      if (dropTarget && dragState.activeCase) {
        if (dropTarget.type === 'transporter' && dropTarget.transporterId) {
          // ドロップ先の陸送員を検索
          const transporter = availableTransporters.find(t => t.id === dropTarget.transporterId);

          // 簡易チェック: 陸送員が存在し、その日のスケジュールが登録されているか確認
          // 詳細なバリデーション（時間重複など）は handleDragEnd で実施
          const canDrop = !!transporter;

          setDragState(prevState =>
            DragDropService.updateDragOverState(prevState, dropTarget, canDrop, '')
          );
        } else {
          // 未割り当てエリアは常にドロップ可能
          setDragState(prevState =>
            DragDropService.updateDragOverState(prevState, dropTarget, true, '')
          );
        }
      } else {
        // ドロップ先がない場合はリセット
        setDragState(prevState => DragDropService.updateDragOverState(prevState, null, true, ''));
      }
    },
    [dragState.activeCase, availableTransporters]
  );

  /**
   * ドラッグ終了時のハンドラー
   */
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      try {
        const result = DragDropService.handleDragEnd(event);

        // 状態をリセット
        setDragState(DragDropService.resetState);

        if (!result) {
          return; // 無効な操作
        }

        // バリデーションチェック（陸送員への割り当ての場合のみ）
        if (result.targetTransporterId) {
          const validationResult = await ScheduleValidationService.validateCaseAssignment(
            result.case,
            result.targetTransporterId,
            cases,
            selectedDate,
            availableTransporters
          );

          if (!validationResult.canAssign) {
            // バリデーションエラー
            if (onValidationError) {
              onValidationError(validationResult);
            }
            return;
          }
        }

        // 案件を更新
        await onCaseUpdate(result.case.id, result.updates);

        // 操作完了コールバック
        if (onOperationComplete) {
          onOperationComplete(result);
        }
      } catch (error) {
        // エラーログ出力（本番環境では適切なエラートラッキングサービスに送信）
        if (import.meta.env.MODE === 'development') {
          logger.error('ドラッグ&ドロップ操作に失敗しました:', error);
        }

        // エラー時も状態をリセット
        setDragState(DragDropService.resetState);
      }
    },
    [
      cases,
      selectedDate,
      availableTransporters,
      onCaseUpdate,
      onOperationComplete,
      onValidationError,
    ]
  );

  /**
   * ドラッグ状態をリセットする
   */
  const resetDragState = useCallback(() => {
    setDragState(DragDropService.resetState);
  }, []);

  /**
   * 案件の割り当て可能性をバリデーションする
   */
  const validateCaseAssignment = useCallback(
    async (case_: Case, transporterId: string): Promise<ValidationResult> => {
      return ScheduleValidationService.validateCaseAssignment(
        case_,
        transporterId,
        cases,
        selectedDate,
        availableTransporters
      );
    },
    [cases, selectedDate, availableTransporters]
  );

  return {
    ...dragState,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    resetDragState,
    validateCaseAssignment,
  };
};
